import type { IObservableArray } from 'mobx';

import type { Goal as GoalModel, Segment, Segments as SegmentsCollection } from '@feathr/blackbox';
import { ECampaignObjective } from '@feathr/blackbox';
import { hasDuplicates } from '@feathr/hooks';
import type { TValidateGrouped } from '@feathr/rachis';

export interface IStepGoalsErrors extends TValidateGrouped {
  goals?: string[];
}

/**
 * Parameters for validating campaign goals.
 */
export interface IValidateStepGoalsParams {
  /** The list of goals to validate */
  goals: IObservableArray<GoalModel>;
  /** Optional segments associated with the goals */
  goalSegments?: Segment[];
  /** Campaign objective - used for v2 validation to enforce minimum goals */
  campaignObjective?: ECampaignObjective;
}

const maxGoalError = `You may not assign more than five goals to a single campaign.
 If you need to capture many different activities as conversions, consider
 using the Advanced conversion tracking mode`;

const minGoalError = 'At least one goal is required for this campaign objective.';

/**
 * Validates goals for a campaign step.
 *
 * @param params - The validation parameters
 * @returns Validation errors object with goals errors array
 *
 * @example
 * validateStepGoals({ goals, goalSegments });
 *
 * @example
 * // With campaign objective and v2 flag
 * validateStepGoals({
 *   goals,
 *   goalSegments,
 *   campaignObjective: ECampaignObjective.Conversions,
 * });
 */
export function validateStepGoals({
  goals,
  goalSegments,
  campaignObjective,
}: IValidateStepGoalsParams): IStepGoalsErrors {
  const activeGoals = goals.filter((g) => !g.get('is_archived'));

  const requiresMinimumGoal = [
    ECampaignObjective.HighDemand,
    ECampaignObjective.Conversions,
  ].includes(campaignObjective as ECampaignObjective);

  if (activeGoals.length === 0) {
    if (requiresMinimumGoal) {
      return { goals: [minGoalError] };
    }
    return { goals: [] };
  }

  if (activeGoals.length >= 6) {
    return { goals: [maxGoalError] };
  }

  const errorMessages: IStepGoalsErrors = { goals: [] };
  errorMessages.goals = activeGoals
    .map((g) => {
      const errors = g.validate(['segment', 'conv_value', 'name'], false).errors;
      if (errors.length > 0) {
        return errors[0];
      }
      return undefined;
    })
    .filter((msg) => !!msg) as string[];

  if (goalSegments) {
    const segmentIds: string[] = [];
    errorMessages.goals = errorMessages.goals.concat(
      goalSegments
        .map((g) => {
          segmentIds.push(g.id);
          const errors = g.validate([], false).errors;
          if (!(g.get('predicates', []) || []).every((p) => p.kind !== 'attribute')) {
            errors.push('Groups with filters on person attributes cannot be used as goal groups.');
          }
          if (errors.length > 0) {
            return errors[0];
          }

          return undefined;
        })
        .filter((msg) => !!msg) as string[],
    );
    if (hasDuplicates(segmentIds)) {
      errorMessages.goals = errorMessages.goals.concat(
        'Must not use the same group for additional goals.',
      );
    }
  }

  return errorMessages;
}

/**
 * Retrieves segment objects for non-archived goals that have segments assigned.
 *
 * @param goals - The list of goals
 * @param Segments - The Segments collection to fetch from
 * @returns Array of Segment objects for goals with segments
 */
export function getGoalSegments(
  goals: IObservableArray<GoalModel>,
  Segments: SegmentsCollection,
): Segment[] {
  return goals
    .filter((g) => !!g.get('segment') && !g.get('is_archived'))
    .map((g) => Segments.get(g.get('segment')!));
}
